import { Injectable } from '@angular/core';
import { ReplaySubject, Subject, filter } from 'rxjs';
import { Author } from '../../models/author.model';
import { BlogPost } from '../../models/blog-posts.model';
import { BlogService } from '../../services/blog/blog.service';
import { RouterService } from '../../shared/services/router.service';
import { PageType } from './fs.helpers';
import { Options } from './fs.interface';

declare const TPP_SNAP: any;

interface ChangedContent {
    node: HTMLElement;
    previewId: string;
    content: any;
}

/**
 * TODO: Make this service optional and provide it only in preview
 */
@Injectable({
    providedIn: 'root',
})
export class FsService {
    private rerenderViewSubject = new Subject<void>();
    private contentChangeSubject = new ReplaySubject<ChangedContent>(1);
    private requestPreviewElementSubject = new Subject<string>();
    private ocmInitialized = new ReplaySubject<void>(1);
    private addedPageType?: PageType;
    private _previewLanguage = '';
    private _previewCountry = '';

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly rerenderView = this.rerenderViewSubject.asObservable();
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly contentChange = this.contentChangeSubject.asObservable();
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly requestPreviewElement = this.requestPreviewElementSubject.asObservable();

    public constructor(
        private routerService: RouterService,
        private blogService: BlogService
    ) {}

    public set previewLanguage(language: string) {
        this._previewLanguage = language;
    }

    public get previewLanguage() {
        return this._previewLanguage;
    }

    public set previewCountry(country: string) {
        this._previewCountry = country;
    }

    public get previewCountry() {
        return this._previewCountry;
    }

    public addAuthor(name: string): void {
        this.addedPageType = PageType.AUTHOR;
        TPP_SNAP.createPage('authors/', name, 'pt_blog_author');
    }

    public addArticle(name: string): void {
        this.addedPageType = PageType.ARTICLE;
        TPP_SNAP.createPage('posts/', name, 'pt_blog_entry');
    }

    public addSection(previewId: string): void {
        TPP_SNAP.setPreviewElement(previewId);
        TPP_SNAP.createSection(previewId, { body: 'content' });
    }

    public setPreviewElement(previewId: string): void {
        this.ocmInitialized.subscribe(() => {
            TPP_SNAP.setPreviewElement(previewId);
        });
    }

    public clearPreviewElement(): void {
        this.ocmInitialized.subscribe(() => {
            TPP_SNAP.setPreviewElement(null);
        });
    }

    public createDataset(template: string, options?: Options) {
        if (typeof TPP_SNAP === 'undefined') {
            return;
        }

        TPP_SNAP.createDataset(template, options);
    }

    public initOCM(): void {
        if (typeof TPP_SNAP !== 'undefined') {
            // use the "old" base64 encoded previewIds instead of the GIDs that are usually used in CaaS v3
            TPP_SNAP.setGidPreviewIdEnabled(false);

            TPP_SNAP.onInit(async (success: any) => {
                console.log(`FirstSpirit Preview is ${success ? 'now' : 'NOT'} available!`);
            });

            // onRerenderView is called when the order of sections is changed
            TPP_SNAP.onRerenderView(async () => {
                this.rerenderViewSubject.next();
            });

            TPP_SNAP.onContentChange(($node: HTMLElement, previewId: string, content: any) => {
                this.contentChangeSubject.next({ node: $node, previewId, content });
            });

            TPP_SNAP.onNavigationChange((handler: string) => {
                if (this.addedPageType === undefined) {
                    return;
                }

                switch (this.addedPageType) {
                    case PageType.ARTICLE:
                        this.blogService
                            .getPostByPreviewId(handler)
                            .pipe(filter((article): article is BlogPost => article !== null))
                            .subscribe((article) => this.routerService.navigateArticle(article));
                        break;
                    case PageType.AUTHOR:
                        this.blogService
                            .getAuthorByPreviewId(handler)
                            .pipe(filter((author): author is Author => author !== null))
                            .subscribe((author) => this.routerService.navigateAuthor(author));
                        break;
                    default:
                        // eslint-disable-next-line no-case-declarations
                        const exhaustiveCheck: never = this.addedPageType;
                        throw new Error(`Unkown action type: ${exhaustiveCheck}`);
                }
            });

            TPP_SNAP.onRequestPreviewElement((previewId: string) => {
                this.requestPreviewElementSubject.next(previewId);
            });

            this.ocmInitialized.next();
        } else {
            console.error('TPP_SNAP was not loaded properly');
        }
    }
}
